import React from "react";
import Spinner from "react-bootstrap/Spinner";

const ChallanCode = ({ value, highlight }) => (
  <code className={highlight ? "text-success fw-bold" : ""}>{value || "—"}</code>
);

function ChallanCorrectorCompare({ filterLabel, before, after, searchLoading, lastApplied }) {
  return (
    <>
      <div className="d-flex flex-wrap align-items-center justify-content-between mb-2 gap-2">
        <div>
          <h5 className="mb-0">Month comparison — {filterLabel}</h5>
          <p className="text-muted small mb-0">
            Active rows only (Is_Deleted = 0, Is_Active = 1). Side-by-side before vs after.
          </p>
        </div>
        {searchLoading && <Spinner animation="border" size="sm" />}
      </div>

      <div className="row g-2 mb-3 small fw-semibold text-center d-none d-lg-flex">
        <div className="col-lg-6 text-secondary">Before correction (current DB)</div>
        <div className="col-lg-6 text-success">
          After correction{lastApplied ? ` — applied ${lastApplied.at}` : " (preview)"}
        </div>
      </div>

      <TablePair
        title="tblChalanHead"
        beforeRows={before.ChalanHead}
        afterRows={after.ChalanHead}
        beforeCols={[
          { key: "ID", render: (r) => r.ID },
          { key: "ch", render: (r) => <ChallanCode value={r.Challan_No_Old} /> },
          { key: "type", render: (r) => r.Reference_ID_Type || "—" },
          { key: "due", render: (r) => r.Due_Date || "—" },
          { key: "paid", render: (r) => (r.Is_Paid ? "Y" : "N") },
        ]}
        afterCols={[
          { key: "ID", render: (r) => r.ID },
          { key: "old", render: (r) => <ChallanCode value={r.Challan_No_Old} /> },
          { key: "new", render: (r) => <ChallanCode value={r.Challan_No_New} highlight={r.WillChange} /> },
          { key: "type", render: (r) => r.Reference_ID_Type || "—" },
          { key: "paid", render: (r) => (r.Is_Paid ? "Y" : "N") },
        ]}
        beforeHeaders={["ID", "Challan", "Type", "Due", "Paid"]}
        afterHeaders={["ID", "Old", "New", "Type", "Paid"]}
        rowKey={(r) => r.ID}
        afterRowKey={(r) => `ah-${r.ID}`}
      />

      <TablePair
        title="tblADLedger"
        beforeRows={before.ADLedger}
        afterRows={after.ADLedger}
        beforeCols={[
          { key: "ID", render: (r) => r.ID },
          { key: "fee", render: (r) => r.FeeCode },
          { key: "ch", render: (r) => <ChallanCode value={r.Challan_No_Old} /> },
          { key: "post", render: (r) => r.PostingDate },
          { key: "recv", render: (r) => r.ReceivedAmount },
        ]}
        afterCols={[
          { key: "ID", render: (r) => r.ID },
          { key: "fee", render: (r) => r.FeeCode },
          { key: "old", render: (r) => <ChallanCode value={r.Challan_No_Old} /> },
          { key: "new", render: (r) => <ChallanCode value={r.Challan_No_New} highlight={r.WillChange} /> },
          { key: "post", render: (r) => r.PostingDate },
        ]}
        beforeHeaders={["ID", "Fee", "Challan", "Post", "Recv"]}
        afterHeaders={["ID", "Fee", "Old", "New", "Post"]}
        rowKey={(r) => r.ID}
        afterRowKey={(r) => `al-${r.ID}`}
      />

      <TablePair
        title="tblScrollEntry"
        beforeRows={before.ScrollEntry}
        afterRows={after.ScrollEntry}
        beforeCols={[
          { key: "id", render: (r) => r.Entry_Id },
          { key: "ch", render: (r) => <ChallanCode value={r.Challan_No_Old} /> },
          { key: "date", render: (r) => r.Dated },
          { key: "amt", render: (r) => r.Amount_Deposited },
          { key: "post", render: (r) => (r.Posted_In_Ledger ? "Y" : "N") },
        ]}
        afterCols={[
          { key: "id", render: (r) => r.Entry_Id },
          { key: "old", render: (r) => <ChallanCode value={r.Challan_No_Old} /> },
          { key: "new", render: (r) => <ChallanCode value={r.Challan_No_New} highlight={r.WillChange} /> },
          { key: "date", render: (r) => r.Dated },
          { key: "amt", render: (r) => r.Amount_Deposited },
        ]}
        beforeHeaders={["Id", "Challan", "Date", "Amt", "Post"]}
        afterHeaders={["Id", "Old", "New", "Date", "Amt"]}
        rowKey={(r) => r.Entry_Id}
        afterRowKey={(r) => `as-${r.Entry_Id}`}
      />
    </>
  );
}

function TablePair({
  title,
  beforeRows,
  afterRows,
  beforeHeaders,
  afterHeaders,
  beforeCols,
  afterCols,
  rowKey,
  afterRowKey,
}) {
  const beforeList = beforeRows || [];
  const afterList = afterRows || [];

  return (
    <div className="mb-3">
      <h6 className="border-bottom pb-1 mb-2">{title}</h6>
      <div className="row g-3">
        <div className="col-lg-6">
          <div className="card h-100 border-secondary">
            <div className="card-header py-1 bg-light small d-lg-none">Before</div>
            <div className="card-body p-2 table-responsive">
              <MiniTable
                headers={beforeHeaders}
                rows={beforeList}
                cols={beforeCols}
                rowKey={rowKey}
                emptyColSpan={beforeHeaders.length}
                highlightMode="warn"
              />
            </div>
          </div>
        </div>
        <div className="col-lg-6">
          <div className="card h-100 border-success">
            <div className="card-header py-1 bg-success bg-opacity-10 small d-lg-none">After</div>
            <div className="card-body p-2 table-responsive">
              <MiniTable
                headers={afterHeaders}
                rows={afterList}
                cols={afterCols}
                rowKey={afterRowKey}
                emptyColSpan={afterHeaders.length}
                highlightMode="success"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MiniTable({ headers, rows, cols, rowKey, emptyColSpan, highlightMode }) {
  const rowClass =
    highlightMode === "warn"
      ? "table-warning"
      : highlightMode === "success"
        ? "table-success"
        : "";

  return (
    <table className="table table-sm table-bordered mb-0">
      <thead className="table-light">
        <tr>
          {headers.map((h) => (
            <th key={h}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td colSpan={emptyColSpan} className="text-center text-muted">
              No rows
            </td>
          </tr>
        ) : (
          rows.map((r) => (
            <tr key={rowKey(r)} className={r.WillChange ? rowClass : ""}>
              {cols.map((c) => (
                <td key={c.key}>{c.render(r)}</td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
}

export default ChallanCorrectorCompare;
